import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { CalendarIcon } from 'lucide-react';
import {
  DataTable,
  EmptyState,
  PageHeading,
  Pagination,
  Panel,
  SegmentedControl,
  SkeletonTable,
} from '@bcecloud/portal-ui';
import { getToken } from '@/lib/auth';
import type { Event, EventListResponse } from '@/types/event';

/** Rows per page — matches the server default. */
const PAGE_SIZE = 10;

/**
 * Formats a date string as a short readable date.
 *
 * @param dateStr - ISO date.
 * @returns e.g. "Aug 8, 2026".
 */
function formatDate(dateStr?: string): string {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/**
 * Builds a location string from venue fields.
 *
 * @param venue - The event's starting venue.
 * @returns "Venue, City, ST" or an em dash.
 */
function formatLocation(venue?: Event['startVenue']): string {
  if (!venue) return '—';
  const parts = [venue.name, [venue.city, venue.state].filter(Boolean).join(', ')].filter(Boolean);
  return parts.join(', ') || '—';
}

/**
 * Builds a time range string with timezone.
 *
 * @param event - The event.
 * @returns "9:00 AM - 12:00 PM EST" or an em dash.
 */
function formatTimeRange(event: Event): string {
  if (!event.timeStartString) return '—';
  let result = event.timeStartString;
  if (event.timeEndString) result += ` - ${event.timeEndString}`;
  if (event.timeZone) result += ` ${event.timeZone}`;
  return result;
}

/** Pagination shape the list endpoint returns. */
interface ListPagination {
  readonly page: number;
  readonly total: number;
  readonly totalPages: number;
}

/**
 * The events list: upcoming and past, as the standard BCE table.
 */
export function EventsPage(): React.ReactElement {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'upcoming' | 'past'>('upcoming');
  const [events, setEvents] = useState<Event[]>([]);
  const [pagination, setPagination] = useState<ListPagination | undefined>();
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setPage(1);
  }, [activeTab]);

  useEffect(() => {
    let cancelled = false;
    async function fetchEvents(): Promise<void> {
      setLoading(true);
      try {
        const token = getToken();
        const { data } = await axios.get<EventListResponse>('/api/v3/events', {
          params: { state: activeTab, page, limit: PAGE_SIZE },
          headers: { Authorization: `Bearer ${token}` },
        });
        if (cancelled) return;
        setEvents(data.data);
        setPagination({
          page: data.pagination.page,
          total: data.pagination.total,
          totalPages: data.pagination.totalPages,
        });
      } catch {
        if (!cancelled) setEvents([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void fetchEvents();
    return () => {
      cancelled = true;
    };
  }, [activeTab, page]);

  return (
    <section className="animate-portal-rise">
      <PageHeading
        eyebrow="EVENT MANAGEMENT"
        title="My events"
        subtitle="Your events with Best Corporate Events — everything you need before, during and after."
        action={
          <SegmentedControl
            label="Show events"
            options={[
              { key: 'upcoming', label: 'Upcoming' },
              { key: 'past', label: 'Past' },
            ]}
            value={activeTab}
            onChange={(key) => setActiveTab(key as 'upcoming' | 'past')}
          />
        }
      />

      {loading ? (
        <SkeletonTable rows={PAGE_SIZE} columns={['w-[12%]', 'w-[30%]', 'w-[20%]']} label="Loading events…" />
      ) : events.length === 0 ? (
        <Panel>
          <EmptyState
            icon={<CalendarIcon aria-hidden className="size-6" />}
            title={activeTab === 'upcoming' ? 'No upcoming events' : 'No past events'}
            message={
              activeTab === 'upcoming'
                ? 'You have no upcoming events scheduled. Your BCE team will add them as they are booked.'
                : 'No past events found.'
            }
          />
        </Panel>
      ) : (
        <>
          <DataTable
            headers={['Contract #', 'Event', 'Date & time', 'Location', 'Participants']}
            minWidth={760}
          >
            {events.map((event) => (
              <tr
                key={event._id}
                onClick={() => navigate(`/events/${event._id}`)}
                className="cursor-pointer border-t border-portal-line align-middle text-portal-tiny transition-colors hover:bg-portal-bg"
              >
                <td className="px-4 py-3 whitespace-nowrap font-mono text-portal-micro text-portal-muted">
                  {event.contractNumber ?? '—'}
                </td>
                <td className="px-4 py-3">
                  <span className="block text-portal-small font-bold">
                    {event.programDisplayName || event.programName || event.opportunityName || '—'}
                  </span>
                  <span className="block text-portal-micro text-portal-muted">
                    {event.account?.name ?? ''}
                  </span>
                </td>
                <td className="px-4 py-3 whitespace-nowrap">
                  <span className="block">{formatDate(event.eventDate)}</span>
                  <span className="block text-portal-micro text-portal-muted">
                    {formatTimeRange(event)}
                  </span>
                </td>
                <td className="max-w-[260px] truncate px-4 py-3 text-portal-muted">
                  {formatLocation(event.startVenue)}
                </td>
                <td className="px-4 py-3 whitespace-nowrap tabular-nums">
                  {event.participants ?? '—'}
                </td>
              </tr>
            ))}
          </DataTable>

          {pagination && pagination.totalPages > 1 ? (
            <Pagination
              page={pagination.page}
              totalPages={pagination.totalPages}
              onChange={setPage}
              summary={`${pagination.total} event${pagination.total === 1 ? '' : 's'}`}
            />
          ) : null}
        </>
      )}
    </section>
  );
}
